import {
  COMPANY_ADDRESS_FULL,
  COMPANY_INITIALS,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_SHORT,
  HERO_IMAGE_URL,
  MAP_URL,
  PROGRAM_PILLARS,
  PROFILE_FACTS,
  PUBLIC_INFO,
  COURSE_NOTES,
} from './constants';

function Header() {
  const navItems = [
    { label: 'Mission', href: '#mission' },
    { label: 'Programs', href: '#programs' },
    { label: 'Profile', href: '#profile' },
    { label: 'Location', href: '#location' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-line/70 bg-cream/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between">
        <a href="#top" className="flex items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-fairway text-[10px] font-black tracking-[0.18em] text-sand shadow-ring">
            {COMPANY_INITIALS}
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brass">Foundation</span>
            <span className="font-serif text-lg font-black text-pine">{COMPANY_SHORT}</span>
          </span>
        </a>
        <nav className="flex flex-wrap items-center gap-1 text-sm font-bold text-pine/80" aria-label="Primary">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-full px-4 py-2 transition hover:bg-fairway hover:text-cream"
            >
              {item.label}
            </a>
          ))}
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="ml-2 rounded-full bg-brass px-5 py-2 text-sm font-black text-pine shadow-sm transition hover:bg-pine hover:text-sand"
          >
            Open Map
          </a>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-pine text-cream">
      <div className="absolute inset-0 bg-fairway-grain opacity-90" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:py-28">
        <div className="text-center">
          <p className="text-xs font-black uppercase tracking-[0.4em] text-brass">
            Southern Ohio PGA &middot; Charitable Foundation
          </p>
          <h1 className="mx-auto mt-6 max-w-5xl font-serif text-4xl font-black leading-[1.05] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            {COMPANY_NAME}
          </h1>
          <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-sand/85">
            A restrained public profile for the section foundation that supports junior golf development, scholarships,
            and charitable giving across Southern Ohio.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="#mission"
              className="rounded-full bg-brass px-7 py-3 font-black text-pine shadow-sm transition hover:bg-cream"
            >
              View Mission
            </a>
            <a
              href="#programs"
              className="rounded-full border border-sand/40 px-7 py-3 font-bold text-sand transition hover:border-brass hover:text-brass"
            >
              Explore Programs
            </a>
          </div>
        </div>

        <div className="relative mt-2 overflow-hidden rounded-3xl border border-brass/30 shadow-soft">
          <img
            src={HERO_IMAGE_URL}
            alt="Southern Ohio fairway with morning light"
            className="h-[360px] w-full object-cover sm:h-[440px]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-pine/90 via-pine/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-7">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brass">Headquarters</p>
            <p className="mt-2 font-serif text-2xl font-black text-cream">{COMPANY_ADDRESS_FULL}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScoreCard() {
  return (
    <section aria-label="Profile scorecard" className="border-b border-line bg-sand/40">
      <div className="mx-auto max-w-7xl px-5 py-10">
        <div className="grid divide-y divide-line rounded-2xl border border-line bg-cream md:grid-cols-4 md:divide-x md:divide-y-0">
          {PROFILE_FACTS.map((fact, idx) => (
            <div key={fact.label} className="flex items-start gap-4 p-6">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-brass/40 font-serif text-sm font-black text-brass">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-fairway">{fact.label}</p>
                <p className="mt-2 font-serif text-lg font-black leading-snug text-pine">{fact.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  return (
    <section id="mission" className="bg-cream">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-brass">Mission</p>
          <h2 className="mt-4 font-serif text-3xl font-black text-pine sm:text-5xl">
            A foundation built around the game.
          </h2>
          <p className="mt-6 leading-7 text-muted">
            The Foundation operates as the charitable arm associated with the Southern Ohio PGA Section &mdash;
            framed here as a public profile, with no transactional, donation, or operational pages.
          </p>
        </div>

        <ul className="grid gap-4 lg:col-span-7">
          {COURSE_NOTES.map((note) => (
            <li
              key={note.title}
              className="grid gap-3 rounded-2xl border border-line bg-sand/30 p-6 md:grid-cols-[140px_1fr] md:items-center"
            >
              <span className="inline-flex w-fit items-center rounded-full bg-fairway px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-sand">
                {note.tag}
              </span>
              <div>
                <h3 className="font-serif text-xl font-black text-pine">{note.title}</h3>
                <p className="mt-2 leading-7 text-muted">{note.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ProgramsSection() {
  return (
    <section id="programs" className="relative bg-pine text-cream">
      <div className="absolute inset-0 bg-fairway-grain opacity-80" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 py-20">
        <div className="flex flex-col gap-6 border-b border-sand/15 pb-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-black uppercase tracking-[0.3em] text-brass">Programs</p>
            <h2 className="mt-4 font-serif text-3xl font-black text-cream sm:text-5xl">
              Four pillars of foundation work.
            </h2>
          </div>
          <p className="max-w-sm text-sm font-bold uppercase tracking-[0.22em] text-sand/70">
            Junior golf, scholarships, community giving, section support.
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {PROGRAM_PILLARS.map((item) => (
            <article
              key={item.title}
              className="group flex h-full flex-col gap-5 rounded-2xl border border-sand/20 bg-cream/[0.04] p-7 transition hover:border-brass/60 hover:bg-cream/[0.08]"
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-3xl font-black text-brass">{item.number}</span>
                <span className="h-px flex-1 translate-y-[1px] bg-sand/20 ml-4" />
              </div>
              <h3 className="font-serif text-xl font-black leading-snug text-cream">{item.title}</h3>
              <p className="text-sm leading-7 text-sand/80">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProfileSection() {
  return (
    <section id="profile" className="bg-sand/40">
      <div className="mx-auto max-w-7xl px-5 py-20">
        <div className="flex flex-col gap-3 text-center">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-brass">Public Profile</p>
          <h2 className="mx-auto max-w-3xl font-serif text-3xl font-black text-pine sm:text-5xl">
            Source-backed details only.
          </h2>
          <p className="mx-auto mt-2 max-w-2xl leading-7 text-muted">
            Identifiers tied to private registry or tax records are intentionally omitted. The page keeps the supplied
            mailing address and section context visible for directory and correspondence use.
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {PUBLIC_INFO.map((item) => (
            <div
              key={item.label}
              className="relative overflow-hidden rounded-2xl border border-line bg-cream p-7 shadow-soft"
            >
              <span className="absolute right-5 top-5 h-2 w-2 rounded-full bg-brass" />
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-fairway">{item.label}</p>
              <p className="mt-3 font-serif text-xl font-black leading-snug text-pine">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationBand() {
  return (
    <section id="location" className="bg-fairway text-cream">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-[1fr_1.1fr] lg:items-center">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.3em] text-brass">Location</p>
          <h2 className="mt-4 font-serif text-3xl font-black sm:text-5xl">{COMPANY_LOCATION}</h2>
          <p className="mt-5 max-w-xl leading-7 text-sand/85">
            The foundation address below is provided for public profile reference, partner directory listings, and
            general correspondence.
          </p>
        </div>
        <div className="rounded-3xl border border-brass/40 bg-pine/60 p-8 backdrop-blur">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-sand/70">Mailing Address</p>
          <p className="mt-4 font-serif text-3xl font-black leading-tight text-cream">{COMPANY_ADDRESS_FULL}</p>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-7 inline-flex rounded-full bg-brass px-7 py-3 font-black text-pine transition hover:bg-cream"
          >
            Open map
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-pine text-sand/70">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-8 text-sm md:flex-row md:items-center md:justify-between">
        <p className="font-bold text-cream">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <p>{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-cream text-ink">
      <a
        href="#mission"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-fairway focus:px-3 focus:py-2 focus:text-cream"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <ScoreCard />
        <MissionSection />
        <ProgramsSection />
        <ProfileSection />
        <LocationBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
